/**
 * FollowUpChat - Post-verdict chat component with quick responses and research deeper
 *
 * Features:
 * - Quick chat responses using existing context (fast)
 * - "Research Deeper" button for full research (multi-agent)
 * - Model selection for quick chat
 * - Message history display
 * - Confidence indicators
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2, Zap, Search, ChevronDown, Bot, Settings2, Coins, Brain, Lock, Paperclip, X, FileText, Maximize2, Minimize2 } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import type { UploadedFile } from '@/types';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { MarkdownContent } from './MarkdownContent';
import { getBestAvailableIntensity, hasMinimumCredits, isIntensityAllowed } from './IntensitySelector';
import type { ResearchIntensity } from '@/types';
import { INTENSITY_CONFIGS, INTENSITY_CREDIT_THRESHOLDS } from '@/types';
import type { ResearchMode } from '@/types/researchModes';

// Chat mode types
type ChatMode = 'quick' | 'multi-agent';

interface ChatModel {
  id: string;
  name: string;
  provider: string;
}

interface Message {
  id: string;
  role: 'user' | 'council' | 'system';
  content: string;
  type: 'query' | 'verdict' | 'followup' | 'chat';
  confidence?: 'high' | 'medium' | 'low';
  suggestResearch?: boolean;
  modelUsed?: string;
  createdAt: string;
}

interface FollowUpChatProps {
  sessionId: string;
  initialMessages?: Message[];
  previousVerdict?: string; // Kept for API compatibility but no longer displayed
  onResearchDeeper: (query: string, intensity: ResearchIntensity, mode: ResearchMode) => void;
  isResearching?: boolean;
  className?: string;
  /** User's current credit balance for intensity restrictions */
  creditBalance?: number | null;
  /** Whether user has unlimited credits */
  isUnlimited?: boolean;
  /** User tier for intensity restrictions */
  userTier?: 'free' | 'trial' | 'standard' | 'master';
  /** Whether the chat is expanded to fullscreen */
  isExpanded?: boolean;
  /** Callback to toggle expand/collapse */
  onToggleExpand?: () => void;
  /** Text selected in research results to prefill as a blockquote */
  quotedText?: string | null;
  /** Callback to clear the quoted text after it's consumed */
  onQuoteClear?: () => void;
}

export function FollowUpChat({
  sessionId,
  initialMessages = [],
  previousVerdict: _previousVerdict, // Destructure but prefix with _ to indicate intentionally unused
  onResearchDeeper,
  isResearching = false,
  className,
  creditBalance,
  isUnlimited = false,
  userTier: _userTier = 'trial', // Prefixed with _ to indicate intentionally unused (reserved for future tier-specific features)
  isExpanded = false,
  onToggleExpand,
  quotedText,
  onQuoteClear,
}: FollowUpChatProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>('quick');
  const [availableModels, setAvailableModels] = useState<ChatModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('openai/gpt-5.2');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showIntensityDropdown, setShowIntensityDropdown] = useState(false);
  // Default to best available intensity based on credit balance
  const [researchIntensity, setResearchIntensity] = useState<ResearchIntensity>(() =>
    getBestAvailableIntensity(creditBalance, isUnlimited, true)
  );
  const [sessionFiles, setSessionFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const intensityDropdownRef = useRef<HTMLDivElement>(null);

  // Check if user has minimum credits to perform any research
  const canResearch = hasMinimumCredits(creditBalance, isUnlimited);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 100; // ~4 lines for the chat input
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Resize on input change
  useEffect(() => {
    autoResize();
  }, [input, autoResize]);

  // Current configs for display
  const currentIntensityConfig = INTENSITY_CONFIGS[researchIntensity];

  const INTENSITY_ICONS: Record<ResearchIntensity, React.ReactNode> = {
    fast: <Zap className="w-3 h-3" />,
    deep: <Brain className="w-3 h-3" />,
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (intensityDropdownRef.current && !intensityDropdownRef.current.contains(event.target as Node)) {
        setShowIntensityDropdown(false);
      }
    };

    if (showIntensityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showIntensityDropdown]);

  // Sync messages state when session changes (when user selects different conversation)
  // This ensures the chat displays the correct history when switching between sessions
  useEffect(() => {
    setMessages(initialMessages);
  }, [sessionId, initialMessages]);

  // Handle quoted text from research content selection
  useEffect(() => {
    if (quotedText && onQuoteClear) {
      setInput(prev => {
        const quote = quotedText.split('\n').map(line => `> ${line}`).join('\n');
        return prev ? `${quote}\n\n${prev}` : `${quote}\n\n`;
      });
      onQuoteClear();
      // Focus the textarea after inserting quote
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [quotedText, onQuoteClear]);

  // Auto-shift intensity based on credit balance changes
  useEffect(() => {
    const bestIntensity = getBestAvailableIntensity(creditBalance, isUnlimited, true);
    const currentThreshold = INTENSITY_CREDIT_THRESHOLDS[researchIntensity];
    const balance = creditBalance ?? 0;

    // Only downgrade if current selection is no longer affordable
    if (!isUnlimited && balance < currentThreshold) {
      setResearchIntensity(bestIntensity);
    }
  }, [creditBalance, isUnlimited, researchIntensity]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await api.getQuickChatModels();
        setAvailableModels(response.models);
        if (response.models.length > 0 && !selectedModel) {
          setSelectedModel(response.models[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        // Fallback models if API fails
        setAvailableModels([
          { id: 'openai/gpt-5.2', name: 'GPT-5.2', provider: 'OpenAI' },
          { id: 'anthropic/claude-opus-4-6', name: 'Claude Opus 4.6', provider: 'Anthropic' },
          { id: 'gemini/gemini-3-pro-preview', name: 'Gemini 3 Pro', provider: 'Google' },
          { id: 'openai/kimi-k2.5', name: 'Kimi K2.5', provider: 'Moonshot' },
        ]);
      }
    };
    fetchModels();
  }, []);

  // Load session files on mount and when session changes
  useEffect(() => {
    const loadSessionFiles = async () => {
      try {
        const files = await api.getSessionFiles(sessionId);
        setSessionFiles(files);
      } catch {
        // Session may not have files yet - that's fine
        setSessionFiles([]);
      }
    };
    loadSessionFiles();
  }, [sessionId]);

  // Handle session file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.csv', '.xlsx', '.xls', '.json', '.xml'];

    // Client-side validation before uploading
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (sessionFiles.length + validFiles.length >= maxFiles) {
        const errorMessage: Message = {
          id: `error-${Date.now()}-limit`,
          role: 'system',
          content: `Max ${maxFiles} files per session. Some files were not uploaded.`,
          type: 'chat',
          createdAt: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        break;
      }
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        const errorMessage: Message = {
          id: `error-${Date.now()}-ext-${i}`,
          role: 'system',
          content: `"${file.name}" - unsupported file type. Allowed: PDF, DOC, TXT, MD, CSV, XLSX, JSON, XML`,
          type: 'chat',
          createdAt: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        continue;
      }
      if (file.size > maxSize) {
        const errorMessage: Message = {
          id: `error-${Date.now()}-size-${i}`,
          role: 'system',
          content: `"${file.name}" exceeds 10MB limit.`,
          type: 'chat',
          createdAt: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    try {
      for (const file of validFiles) {
        const uploaded = await api.uploadSessionFile(sessionId, file);
        setSessionFiles(prev => [...prev, uploaded]);
      }
    } catch (error) {
      console.error('Failed to upload session file:', error);
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'system',
        content: `File upload failed: ${errorMsg}`,
        type: 'chat',
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle session file deletion
  const handleFileDelete = async (fileId: string) => {
    try {
      await api.deleteSessionFile(sessionId, fileId);
      setSessionFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete session file:', error);
    }
  };

  // Helper to strip CONFIDENCE:/NEEDS_RESEARCH: metadata markers from streamed text
  const cleanMetadataMarkers = (text: string): string => {
    let clean = text;
    for (const marker of ['CONFIDENCE:', 'NEEDS_RESEARCH:']) {
      const idx = clean.indexOf(marker);
      if (idx !== -1) {
        clean = clean.substring(0, idx).trim();
      }
    }
    return clean;
  };

  // Handle quick chat submission with streaming
  const handleQuickChat = async () => {
    if (!input.trim() || isLoading || isResearching) return;

    const question = input.trim();
    setInput('');

    // Add user message immediately
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: question,
      type: 'followup',
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Create a placeholder message for streaming
    const streamMsgId = `stream-${Date.now()}`;
    const streamMessage: Message = {
      id: streamMsgId,
      role: 'council',
      content: '',
      type: 'chat',
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, streamMessage]);

    let doneReceived = false;

    api.quickChatStream(
      sessionId,
      question,
      { model: selectedModel },
      {
        onToken: (content) => {
          setMessages(prev => prev.map(msg =>
            msg.id === streamMsgId
              ? { ...msg, content: msg.content + content }
              : msg
          ));
        },
        onMetadata: (data) => {
          const modelInfo = availableModels.find(m => m.id === data.model_used);
          setMessages(prev => prev.map(msg =>
            msg.id === streamMsgId
              ? {
                  ...msg,
                  confidence: data.confidence as 'high' | 'medium' | 'low',
                  suggestResearch: data.suggest_deep_research,
                  modelUsed: modelInfo?.name || data.model_used,
                  content: cleanMetadataMarkers(msg.content),
                }
              : msg
          ));
        },
        onMessageStored: (data) => {
          if (data.message_id) {
            setMessages(prev => prev.map(msg =>
              msg.id === streamMsgId
                ? { ...msg, id: data.message_id }
                : msg
            ));
          }
        },
        onDone: () => {
          if (!doneReceived) {
            doneReceived = true;
            setIsLoading(false);
          }
        },
        onError: (error) => {
          console.error('Quick chat stream error:', error);
          setMessages(prev => prev.map(msg =>
            msg.id === streamMsgId
              ? {
                  ...msg,
                  role: 'system' as const,
                  content: 'Failed to get a response. Try again or use "Research Deeper" for a comprehensive answer.',
                }
              : msg
          ));
          setIsLoading(false);
        },
      }
    );
  };

  // Handle research deeper - passes selected intensity, mode auto-detected by backend
  const handleResearchDeeper = () => {
    if (input.trim()) {
      onResearchDeeper(input.trim(), researchIntensity, 'auto');
      setInput('');
    }
  };

  // Chat messages excluding initial query/verdict (those are shown in the result cards above)
  const chatMessages = messages.filter(
    message => !(message.role === 'user' && message.type === 'query') && message.type !== 'verdict'
  );
  const hasChatMessages = chatMessages.length > 0;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Chat header with expand toggle - only show when there are actual chat messages */}
      {onToggleExpand && hasChatMessages && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border-subtle">
          <span className="text-xs text-text-muted font-medium uppercase tracking-wider">Conversation</span>
          <button
            onClick={onToggleExpand}
            className="p-1.5 rounded-md hover:bg-surface-light text-text-muted hover:text-text-primary transition-colors"
            title={isExpanded ? 'Collapse chat' : 'Expand chat'}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Scrollable messages area - only render when there are messages to show */}
      {hasChatMessages && (
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {chatMessages.map((message) => (
          <MessageBubble key={message.id} message={message} onResearchDeeper={onResearchDeeper} />
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center gap-2 text-text-secondary">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Thinking...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      )}

      {/* Input area - Clean, subtle design inspired by multiple.chat */}
      <div className="border-t border-border-subtle p-4 bg-background/80 backdrop-blur-lg">
        {/* Insufficient Credits Warning - only show in multi-agent mode */}
        {!canResearch && chatMode === 'multi-agent' && (
          <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Coins className="w-4 h-4 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-amber-400 font-medium text-sm">Insufficient Credits</h4>
                <p className="text-text-secondary text-xs">
                  You need at least {INTENSITY_CREDIT_THRESHOLDS.fast} credits to run research.
                </p>
              </div>
              <button
                onClick={() => window.location.href = '/plans'}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 rounded-lg text-amber-400 text-xs font-medium transition-colors flex-shrink-0"
              >
                <Coins className="w-3 h-3" />
                Get Credits
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input for session file uploads */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.json,.xml"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Session file chips */}
        {sessionFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {sessionFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.05] border border-border-subtle rounded-lg text-xs text-text-primary group"
              >
                <FileText className="w-3 h-3 text-text-muted flex-shrink-0" />
                <span className="max-w-[140px] truncate">{file.original_filename}</span>
                <button
                  onClick={() => handleFileDelete(file.id)}
                  className="text-text-muted hover:text-red-400 transition-colors flex-shrink-0"
                  title="Remove file"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input container with integrated controls */}
        <div className={cn(
          "bg-white/[0.03] rounded-2xl border border-border-subtle",
          !canResearch && chatMode === 'multi-agent' && "opacity-50"
        )}>
          {/* Textarea area - full width */}
          <div className="px-4 pt-3 pb-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (chatMode === 'quick') {
                    handleQuickChat();
                  } else if (canResearch) {
                    handleResearchDeeper();
                  }
                }
              }}
              placeholder={
                chatMode === 'quick'
                  ? "Ask a follow-up question..."
                  : canResearch
                    ? "Enter a research question..."
                    : "Purchase credits to continue researching..."
              }
              rows={1}
              aria-label="Chat message"
              className="w-full bg-transparent text-text-primary placeholder-text-muted outline-none text-sm disabled:cursor-not-allowed resize-none leading-normal"
              style={{ overflow: 'hidden' }}
              disabled={isLoading || isResearching || (chatMode === 'multi-agent' && !canResearch)}
            />
          </div>

          {/* Bottom toolbar with controls + submit */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-border-subtle bg-white/[0.02] rounded-b-2xl">
            {/* Mode Toggle - small, subtle pills */}
            <div className="flex items-center gap-1">
              {/* File attach button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || isResearching || isUploading}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all",
                  sessionFiles.length > 0
                    ? "text-verdict-primary bg-verdict-primary/10"
                    : "text-text-muted hover:text-text-primary hover:bg-surface-light",
                  (isLoading || isResearching || isUploading) && "opacity-50 cursor-not-allowed"
                )}
                title={`Attach files to this session (${sessionFiles.length}/5)`}
              >
                {isUploading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Paperclip className="w-3 h-3" />
                )}
                {sessionFiles.length > 0 && (
                  <span className="text-[10px]">{sessionFiles.length}</span>
                )}
              </button>

              <Tooltip content="Fast response from a single AI model using research already gathered. No additional credits." position="top">
                <button
                  onClick={() => setChatMode('quick')}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all",
                    chatMode === 'quick'
                      ? "bg-surface-light text-text-primary"
                      : "text-text-muted hover:text-text-primary hover:bg-surface-light"
                  )}
                >
                  <Zap className="w-3 h-3" />
                  <span>Quick</span>
                </button>
              </Tooltip>
              <Tooltip content={!canResearch ? `Requires at least ${INTENSITY_CREDIT_THRESHOLDS.fast} credits` : "Launch a full multi-agent research session on your follow-up. Uses credits."} position="top">
                <button
                  onClick={() => setChatMode('multi-agent')}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all",
                    chatMode === 'multi-agent'
                      ? "bg-verdict-primary/15 text-verdict-primary"
                      : "text-text-muted hover:text-verdict-primary/70 hover:bg-verdict-primary/5",
                    !canResearch && "opacity-60"
                  )}
                >
                  <Search className="w-3 h-3" />
                  <span>Research</span>
                  {!canResearch && <Coins className="w-2.5 h-2.5 text-amber-500" />}
                </button>
              </Tooltip>

            </div>

            {/* Right side - selectors + submit */}
            <div className="flex items-center gap-2">
              {/* Model Selector - only in quick mode */}
              {chatMode === 'quick' && (
                <div className="relative">
                  <button
                    onClick={() => setShowModelSelector(!showModelSelector)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-text-muted hover:text-text-primary hover:bg-surface-light transition-all"
                  >
                    <Bot className="w-3 h-3" />
                    <span className="max-w-[100px] truncate">{availableModels.find(m => m.id === selectedModel)?.name || 'Model'}</span>
                    <ChevronDown className={cn("w-3 h-3 transition-transform", showModelSelector && "rotate-180")} />
                  </button>

                  {/* Model Dropdown - positioned to open upward, right edge aligned with button */}
                  {showModelSelector && (
                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-surface-raised rounded-xl border border-border-subtle shadow-xl z-50 overflow-hidden">
                      <div className="p-2 border-b border-border-subtle">
                        <div className="flex items-center gap-2 text-[10px] text-text-muted px-2 uppercase tracking-wider">
                          <Settings2 className="w-3 h-3" />
                          Select Model
                          <Tooltip content="Choose which AI model answers your follow-up. GPT-4o Mini is fast and cheap; Claude or GPT-4o give more detailed answers." position="left">
                            <span className="cursor-help text-text-muted hover:text-text-secondary">?</span>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto py-1">
                        {availableModels.map((model) => (
                          <button
                            key={model.id}
                            onClick={() => {
                              setSelectedModel(model.id);
                              setShowModelSelector(false);
                            }}
                            className={cn(
                              "w-full flex items-center justify-between px-3 py-2 text-xs transition-colors",
                              selectedModel === model.id
                                ? "bg-verdict-primary/10 text-verdict-primary"
                                : "text-text-primary hover:bg-surface-light"
                            )}
                          >
                            <span>{model.name}</span>
                            <span className="text-[10px] text-text-muted">{model.provider}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Intensity Dropdown - only in multi-agent research mode */}
              {chatMode === 'multi-agent' && (
                <>
                  {/* Intensity Dropdown */}
                  <div ref={intensityDropdownRef} className="relative">
                    <button
                      onClick={() => setShowIntensityDropdown(!showIntensityDropdown)}
                      disabled={isLoading || isResearching}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all border",
                        "bg-surface-light/50 border-border/50 hover:border-border-light",
                        (isLoading || isResearching) && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {INTENSITY_ICONS[researchIntensity]}
                      <span className="text-text-primary">{currentIntensityConfig.label}</span>
                      <ChevronDown className={cn("w-2.5 h-2.5 text-text-muted transition-transform", showIntensityDropdown && "rotate-180")} />
                    </button>

                    {/* Intensity Dropdown Menu - opens upward */}
                    {showIntensityDropdown && (
                      <div className="absolute bottom-full right-0 mb-2 w-48 bg-surface border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                        <div className="p-1.5 border-b border-border">
                          <span className="text-[9px] text-text-muted uppercase tracking-wider px-2">Depth</span>
                        </div>
                        {(['fast', 'deep'] as ResearchIntensity[]).map((i) => {
                          const config = INTENSITY_CONFIGS[i];
                          const isSelected = i === researchIntensity;
                          const allowed = isIntensityAllowed(i, creditBalance, isUnlimited, true);
                          return (
                            <button
                              key={i}
                              onClick={() => { if (allowed) { setResearchIntensity(i); setShowIntensityDropdown(false); } }}
                              disabled={!allowed}
                              className={cn(
                                "w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors",
                                !allowed && "opacity-50 cursor-not-allowed",
                                isSelected ? "bg-verdict-primary/10 text-verdict-primary" : allowed ? "hover:bg-surface-light/50 text-text-primary" : "text-text-muted"
                              )}
                            >
                              {INTENSITY_ICONS[i]}
                              <span>{config.label}</span>
                              <span className="text-[10px] text-text-muted">({config.agentCount})</span>
                              {!allowed && <Lock className="w-2.5 h-2.5 ml-auto" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Submit button */}
              <button
                onClick={chatMode === 'quick' ? handleQuickChat : handleResearchDeeper}
                disabled={!input.trim() || isLoading || isResearching || (chatMode === 'multi-agent' && !canResearch)}
                className={cn(
                  "p-2 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0",
                  chatMode === 'quick'
                    ? "bg-surface-light hover:bg-surface-light text-text-primary"
                    : "bg-verdict-primary/10 hover:bg-verdict-primary/20 text-verdict-primary"
                )}
                title={chatMode === 'quick' ? "Quick answer" : canResearch ? "Full research" : "Insufficient credits"}
              >
                {(isLoading || isResearching) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : chatMode === 'quick' ? (
                  <Zap className="w-4 h-4" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Message bubble component
function MessageBubble({
  message,
  onResearchDeeper
}: {
  message: Message;
  onResearchDeeper: (query: string, intensity: ResearchIntensity, mode: ResearchMode) => void;
}) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  return (
    <div className={cn(
      "flex",
      isUser ? "justify-end" : "justify-start"
    )}>
      <div className={cn(
        "max-w-[80%] rounded-2xl px-4 py-3",
        isUser
          ? "bg-verdict-primary/20 text-text-primary"
          : isSystem
            ? "bg-red-500/10 text-red-300 border border-red-500/20"
            : "bg-surface-light text-text-primary/90 border border-border-subtle"
      )}>
        {/* Use MarkdownContent for council/verdict messages, plain text for user */}
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        ) : (
          <MarkdownContent content={message.content} className="text-sm" />
        )}

        {/* Confidence indicator and model info for assistant messages */}
        {message.confidence && !isUser && (
          <div className="flex items-center flex-wrap gap-2 mt-2 pt-2 border-t border-border-subtle">
            <Tooltip
              content={
                message.confidence === 'high'
                  ? "Well-supported by research findings"
                  : message.confidence === 'medium'
                    ? "Some relevant information found but the answer may be incomplete"
                    : "Limited evidence found — consider using 'Research' for a more thorough answer"
              }
              position="top"
            >
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full cursor-help",
                message.confidence === 'high' && "bg-green-500/20 text-green-400",
                message.confidence === 'medium' && "bg-yellow-500/20 text-yellow-400",
                message.confidence === 'low' && "bg-red-500/20 text-red-400"
              )}>
                {message.confidence} confidence
              </span>
            </Tooltip>

            {/* Display model used */}
            {message.modelUsed && (
              <span className="text-xs text-text-muted">• {message.modelUsed}</span>
            )}

            {message.suggestResearch && (
              <button
                onClick={() => onResearchDeeper(message.content, 'fast', 'auto')}
                className="text-xs text-verdict-primary hover:underline flex items-center gap-1"
              >
                <Search className="w-3 h-3" />
                Research deeper
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

